import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  InformationBoxComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { PracticePermissions } from '@principle-theorem/principle-core/features';
import {
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, WithRef } from '@principle-theorem/shared';

export interface IHeidiAccountSettingsData {
  practice: WithRef<IPractice>;
  staffer: WithRef<IStaffer>;
}

interface IHeidiAccountSettingsForm {
  practiceEmail: string;
  stafferEmail: string;
  useAsDefault: boolean;
}

@Component({
  selector: 'pr-heidi-account-settings',
  imports: [NgDialogModule, MatCheckboxModule, InformationBoxComponent],
  templateUrl: './heidi-account-settings.component.html',
  styleUrl: './heidi-account-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeidiAccountSettingsComponent {
  private _dialogRef = inject(MatDialogRef);
  private _snackBar = inject(MatSnackBar);
  private _org = inject(OrganisationService);
  data = inject<IHeidiAccountSettingsData>(MAT_DIALOG_DATA);
  form = new TypedFormGroup<IHeidiAccountSettingsForm>({
    practiceEmail: new TypedFormControl<string>(
      this.data.practice.metadata?.heidi?.email,
      Validators.email
    ),
    stafferEmail: new TypedFormControl<string>(
      this.data.staffer.metadata?.heidi?.email,
      Validators.email
    ),
    useAsDefault: new TypedFormControl<boolean>(
      this.data.staffer.metadata?.heidi?.useAsDefault || false
    ),
  });

  constructor() {
    this._org.userPermissions$
      .pipe(takeUntilDestroyed())
      .subscribe((permissions) => {
        if (!permissions.includes(PracticePermissions.PracticeConfigure)) {
          this.form.controls.practiceEmail.disable();
        }
      });
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    const formData = this.form.getRawValue();
    await Firestore.patchDoc(this.data.practice.ref, {
      metadata: {
        ...this.data.practice.metadata,
        heidi: {
          ...this.data.practice.metadata?.heidi,
          email: formData.practiceEmail,
        },
      },
    });
    await Firestore.patchDoc(this.data.staffer.ref, {
      metadata: {
        ...this.data.staffer.metadata,
        heidi: {
          ...this.data.staffer.metadata?.heidi,
          email: formData.stafferEmail,
          useAsDefault: formData.useAsDefault,
        },
      },
    });

    this._snackBar.open('Heidi Account Settings Saved');
    this._dialogRef.close();
  }
}
